import type { GlowBlock } from "@/lib/api";

interface ArticleBodyProps {
  blocks: GlowBlock[];
}

export default function ArticleBody({ blocks }: ArticleBodyProps) {
  return (
    <article
      className="max-w-3xl mx-auto px-6 lg:px-0 py-14"
      style={{ fontFamily: "'Jost', sans-serif" }}
    >
      {blocks.map((block, idx) => {
        switch (block.type) {
          case "paragraph":
            return (
              <p
                key={idx}
                className="text-midnight/75 text-[15px] leading-[1.9] mb-7"
              >
                {block.content}
              </p>
            );

          case "heading":
            return (
              <h2
                key={idx}
                className="text-2xl md:text-3xl font-light italic text-midnight mt-12 mb-5"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                {block.content}
              </h2>
            );

          case "subheading":
            return (
              <h3
                key={idx}
                className="text-xs tracking-widest uppercase text-midnight/50 mt-9 mb-3"
              >
                {block.content}
              </h3>
            );

          case "quote":
            return (
              <blockquote
                key={idx}
                className="my-12 pl-6 border-l-2 border-midnight/20"
              >
                <p
                  className="text-xl md:text-2xl font-light italic text-midnight leading-relaxed"
                  style={{ fontFamily: "'Cormorant Garamond', serif" }}
                >
                  &ldquo;{block.content}&rdquo;
                </p>
              </blockquote>
            );

          case "image":
            return (
              <figure key={idx} className="my-12 -mx-6 lg:-mx-24">
                <div className="w-full overflow-hidden" style={{ aspectRatio: "16/9" }}>
                  <img
                    src={block.src}
                    alt={block.alt}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                {block.caption && (
                  <figcaption className="mt-3 px-6 lg:px-24 text-xs text-midnight/40 italic">
                    {block.caption}
                  </figcaption>
                )}
              </figure>
            );

          case "divider":
            return (
              <div key={idx} className="my-14 flex items-center gap-4">
                <div className="flex-1 h-px bg-midnight/10" />
                <span className="text-midnight/20 text-lg" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  ✦
                </span>
                <div className="flex-1 h-px bg-midnight/10" />
              </div>
            );

          default:
            return null;
        }
      })}
    </article>
  );
}
